import {
	ArrowRightOutlined,
	ClockCircleOutlined,
	HeartOutlined,
	IdcardOutlined,
	InboxOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { Button, Skeleton } from "antd";
import { Link } from "react-router-dom";
import { StatusTag } from "@/modules/aid-records/components/StatusTag";
import { useAidRecordsList } from "@/modules/aid-records/hooks/useAidRecords";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { useItemsList } from "@/modules/items/hooks/useItems";
import { usePersonsList } from "@/modules/persons/hooks/usePersons";
import { EmptyState, PageHeader, StatCard, Surface } from "@/shared/components/ui";
import { formatDateTime } from "@/shared/lib/format";

/** Counts come from the list endpoints' pagination meta — one row is enough. */
const COUNT_ONLY = { page: 1, limit: 1 } as const;

function firstName(fullName?: string | null) {
	return fullName?.trim().split(/\s+/).at(-1) ?? null;
}

export function DashboardPage() {
	const user = useAuthStore((state) => state.user);

	const aidTotal = useAidRecordsList(COUNT_ONLY);
	const aidPending = useAidRecordsList({ ...COUNT_ONLY, status: "pending" });
	const persons = usePersonsList(COUNT_ONLY);
	const items = useItemsList(COUNT_ONLY);
	const recent = useAidRecordsList({ page: 1, limit: 6 });

	const greeting = firstName(user?.fullName);

	return (
		<div>
			<PageHeader
				title={greeting ? `Salom, ${greeting}` : "Umumiy ko'rinish"}
				description="Yordam taqsimoti bo'yicha bugungi holat"
				actions={
					<Link to="/aid-records">
						<Button type="primary" icon={<PlusOutlined />}>
							Yordam qo'shish
						</Button>
					</Link>
				}
			/>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<StatCard
					label="Jami yordamlar"
					value={aidTotal.data?.meta.total ?? 0}
					loading={aidTotal.isLoading}
					icon={<HeartOutlined />}
					tone="accent"
					to="/aid-records"
				/>
				<StatCard
					label="Kutilmoqda"
					value={aidPending.data?.meta.total ?? 0}
					loading={aidPending.isLoading}
					icon={<ClockCircleOutlined />}
					tone="warning"
					hint="Tasdiqlanishi kerak"
					to="/aid-records"
				/>
				<StatCard
					label="Shaxslar"
					value={persons.data?.meta.total ?? 0}
					loading={persons.isLoading}
					icon={<IdcardOutlined />}
					to="/persons"
				/>
				<StatCard
					label="Mahsulotlar"
					value={items.data?.meta.total ?? 0}
					loading={items.isLoading}
					icon={<InboxOutlined />}
					to="/items"
				/>
			</div>

			<Surface flush className="mt-6">
				<div className="flex items-center justify-between border-b border-[var(--c-border-subtle)] px-5 py-3.5">
					<h2 className="m-0 text-sm font-semibold text-[var(--c-text)]">Oxirgi yordamlar</h2>
					<Link
						to="/aid-records"
						className="flex items-center gap-1 text-[13px] font-medium text-[var(--c-accent)] hover:opacity-80"
					>
						Barchasi <ArrowRightOutlined className="text-[10px]" />
					</Link>
				</div>

				{recent.isLoading ? (
					<div className="px-5 py-4">
						<Skeleton active paragraph={{ rows: 4 }} title={false} />
					</div>
				) : recent.data?.items.length ? (
					<ul className="m-0 list-none p-0">
						{recent.data.items.map((record) => (
							<li
								key={record.id}
								className="flex flex-wrap items-center gap-3 border-b border-[var(--c-border-subtle)] px-5 py-3 last:border-b-0"
							>
								<div className="min-w-0 flex-1">
									<div className="truncate text-[13px] font-medium text-[var(--c-text)]">
										{record.person.name}
									</div>
									<div className="truncate text-xs text-[var(--c-text-muted)]">
										{record.item.name} · {record.quantity} · {record.givenBy.name}
									</div>
								</div>
								<span className="numeric hidden text-xs text-[var(--c-text-muted)] sm:block">
									{formatDateTime(record.givenAt)}
								</span>
								<StatusTag status={record.status} />
							</li>
						))}
					</ul>
				) : (
					<EmptyState
						title="Hozircha yordam yozuvlari yo'q"
						description="Birinchi yozuvni qo'shsangiz, u shu yerda ko'rinadi."
						icon={<HeartOutlined />}
						action={
							<Link to="/aid-records">
								<Button icon={<PlusOutlined />}>Yordam qo'shish</Button>
							</Link>
						}
					/>
				)}
			</Surface>
		</div>
	);
}
